import { useEffect, useState } from 'react';
import { Alert, Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import BottomNavigationBar from '../../components/BottomNavigationBar';
import InventoryActivationModal from '../../components/InventoryActivationModal';
import { InventoryCard } from '../../components/InventoryCard';
import UserPageHeader from '../../components/UserPageHeader';
import { type InventoryItem } from '../../service/api';
import { useInventory } from '../../store/inventory';

export function InventoryPage() {
  const { t } = useTranslation();
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { inventory, loading, error, refreshInventory } = useInventory();

  useEffect(() => {
    refreshInventory();
  }, [refreshInventory]);

  const handleActivateItem = (item: InventoryItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  const availableItems = inventory.filter(
    item => item.status === 'available' && item.amount > 0,
  );
  const pendingItems = inventory.filter(
    item => item.status === 'pending_activation',
  );

  return (
    <Container className="pt-3 pb-5">
      <Row>
        <Col xs={12}>
          <UserPageHeader />
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {loading && inventory.length === 0 && (
        <div className="text-center py-5">
          <p className="text-white">{t('common.loading')}</p>
        </div>
      )}

      {!loading && availableItems.length === 0 && pendingItems.length === 0 && (
        <div className="text-center py-5">
          <p className="text-white-50">{t('inventory.empty')}</p>
        </div>
      )}

      {/* Available Items */}
      {availableItems.length > 0 && (
        <div className="mb-4">
          <h3 className="h4 text-white mb-3">
            {t('inventory.sections.available')} ({availableItems.length})
          </h3>
          <Row>
            {availableItems.map(item => (
              <Col key={item._id} xs={12} sm={6} md={4} lg={3} className="mb-3">
                <InventoryCard item={item} onActivate={handleActivateItem} />
              </Col>
            ))}
          </Row>
        </div>
      )}

      {/* Pending Activations */}
      {pendingItems.length > 0 && (
        <div className="mb-4">
          <h3 className="h4 text-white mb-3">
            {t('inventory.sections.pending')} ({pendingItems.length})
          </h3>
          <Row>
            {pendingItems.map(item => (
              <Col key={item._id} xs={12} sm={6} md={4} lg={3} className="mb-3">
                <InventoryCard item={item} />
              </Col>
            ))}
          </Row>
        </div>
      )}

      <BottomNavigationBar />

      {selectedItem && (
        <InventoryActivationModal
          isOpen={isModalOpen}
          onClose={closeModal}
          item={selectedItem}
        />
      )}
    </Container>
  );
}
